export function getDesignRequestBase(pathname: string): string {
  if (pathname.includes("/realtor")) return "/realtor/design-requests";
  if (pathname.includes("/teammember")) return "/teammember/design-requests";
  return "/agent/design-requests";
}

export function getDesignRequestRoleBase(pathname: string): string {
  if (pathname.includes("/realtor")) return "/realtor";
  if (pathname.includes("/teammember")) return "/teammember";
  return "/agent";
}

export function isAgentDesignRequestRole(pathname: string): boolean {
  return pathname.includes("/agent");
}

export function isBrokerageDesignRequestRole(pathname: string): boolean {
  return pathname.includes("/realtor") || pathname.includes("/teammember");
}

const API_URL = process.env.REACT_APP_API_URL || "";

function authHeaders(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    Accept: "application/json",
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export function coerceId(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    if (o.$oid != null) return String(o.$oid).trim();
    if (o.oid != null) return String(o.oid).trim();
    if (o.id != null) return String(o.id).trim();
    if (o._id != null) return coerceId(o._id);
  }
  const s = String(value).trim();
  return s === "[object Object]" ? "" : s;
}

function coerceIso(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
  const o = asRecord(value);
  return String(o.$date ?? o.date ?? o.datetime ?? "").trim();
}

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json();
    const msg = data?.message || data?.error || data?.msg;
    if (typeof msg === "string" && msg.trim()) return msg;
  } catch {
    /* ignore */
  }
  return `${fallback} (${response.status})`;
}

export type ClientProjectListItem = {
  id: string;
  project_number?: string;
  linked_order_id?: string;
  linked_order_number?: string;
  linked_order_type?: "sales_order" | "service_order";
  title?: string;
  agent_id?: string;
  agent_name?: string;
  team_member_name?: string;
  vendor_name?: string;
  designer_name?: string;
  assigned_to_name?: string;
  status?: string;
  payment_status?: string;
  waiting_on?: string;
  last_update?: string;
  last_message?: {
    message?: string;
    sender_type?: string;
    created_at?: string;
  } | null;
  has_unread?: boolean;
};

export type ClientProjectsListCounts = {
  in_progress: number;
  design_approved: number;
  quoted: number;
  completed: number;
  total: number;
};

export type FetchClientProjectsResult = {
  data: ClientProjectListItem[];
  total: number;
  counts: ClientProjectsListCounts | null;
};

function mapListRow(row: unknown): ClientProjectListItem | null {
  const root = asRecord(row);
  const id = coerceId(root.id ?? root._id);
  if (!id) return null;
  const linked = asRecord(root.linked_order);
  return {
    id,
    project_number: String(root.project_number ?? "").trim() || undefined,
    linked_order_id: coerceId(root.linked_order_id ?? linked.id) || undefined,
    linked_order_number: String(root.linked_order_number ?? linked.number ?? "").trim() || undefined,
    linked_order_type: (String(root.linked_order_type ?? "").trim() as ClientProjectListItem["linked_order_type"]) || undefined,
    title: String(root.title ?? "").trim() || undefined,
    agent_id: coerceId(root.agent_id ?? root.agent_membership_id) || undefined,
    agent_name: String(root.agent_name ?? "").trim() || undefined,
    team_member_name: String(root.team_member_name ?? "").trim() || undefined,
    vendor_name: String(root.designer_name ?? root.assigned_to_name ?? root.vendor_name ?? "").trim() || undefined,
    designer_name: String(root.designer_name ?? "").trim() || undefined,
    assigned_to_name: String(root.assigned_to_name ?? "").trim() || undefined,
    status: String(root.status ?? "").trim() || undefined,
    payment_status: String(root.payment_status ?? "").trim() || undefined,
    waiting_on: String(root.waiting_on ?? root.dependency_on ?? "").trim() || undefined,
    last_update: String(root.last_update ?? root.updated_at ?? root.created_at ?? "").trim() || undefined,
    last_message: asRecord(root.last_message).message
      ? {
          message: String(asRecord(root.last_message).message ?? ""),
          sender_type: String(asRecord(root.last_message).sender_type ?? ""),
          created_at: String(asRecord(root.last_message).created_at ?? ""),
        }
      : null,
    has_unread: root.has_unread === true,
  };
}

export async function fetchClientProjectsList(
  token: string,
  params: { page: number; limit: number; search?: string; status?: string }
): Promise<FetchClientProjectsResult> {
  const qs = new URLSearchParams();
  qs.set("page", String(params.page));
  qs.set("limit", String(params.limit));
  if (params.search?.trim()) qs.set("search", params.search.trim());
  if (params.status?.trim()) qs.set("status", params.status.trim());

  const response = await fetch(`${API_URL}/client/projects?${qs.toString()}`, {
    method: "GET",
    headers: authHeaders(token),
  });
  if (!response.ok) throw new Error(await readError(response, "Failed to load design requests"));
  const data = await response.json();
  const root = asRecord(data);
  const nested = asRecord(root.data);
  const rows = Array.isArray(root.data) ? root.data : asArray(nested.data ?? nested.results ?? root.results);
  const countsRaw = asRecord(root.counts ?? nested.counts);
  return {
    data: rows.map(mapListRow).filter((row): row is ClientProjectListItem => !!row),
    total: Number(root.total ?? nested.total ?? rows.length) || 0,
    counts:
      Object.keys(countsRaw).length > 0
        ? {
            in_progress: Number(countsRaw.in_progress) || 0,
            design_approved: Number(countsRaw.design_approved) || 0,
            quoted: Number(countsRaw.quoted) || 0,
            completed: Number(countsRaw.completed) || 0,
            total: Number(countsRaw.total) || 0,
          }
        : null,
  };
}

export async function deleteClientProject(token: string, projectId: string): Promise<void> {
  const response = await fetch(`${API_URL}/projects/${encodeURIComponent(projectId)}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  if (!response.ok) throw new Error(await readError(response, "Failed to delete design request"));
}

export type ClientProjectDetail = {
  id: string;
  project_number?: string;
  title?: string;
  notes?: string;
  status?: string;
  vendor_org_id?: string;
  designer_name?: string;
  assigned_to_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_name?: string;
  first_name?: string;
  last_name?: string;
  customer_global_user_id?: string;
  customer_membership_id?: string;
  agent_global_user_id?: string;
  team_member_global_user_id?: string;
  license_type?: string;
  agent_membership_id?: string;
  agent_name?: string;
  team_member_membership_id?: string;
  team_member_name?: string;
  brokerage_firm_organization_id?: string;
  brokerage_firm_name?: string;
  office?: { _id?: string; officeName?: string };
  created_at?: string;
  updated_at?: string;
  linked_order_id?: string;
  linked_order_number?: string;
  linked_order_type?: string;
  approved_proof_versions?: number[];
  approved_proofs?: any[];
  billing_address?: any;
  shipping_address?: any;
  shipping_method?: { name?: string; description?: string } | null;
  quantity?: number;
  design_approved_by?: { name?: string };
};

export function isDraftLikeProjectStatus(status?: string): boolean {
  const s = String(status || "").trim().toLowerCase();
  return s === "draft" || s === "in_progress";
}

export async function resolveVendorOrganizationName(token: string, organizationId: string): Promise<string> {
  const orgId = String(organizationId || "").trim();
  if (!token || !orgId) return "";
  try {
    const response = await fetch(`${API_URL}/organizations/${encodeURIComponent(orgId)}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    });
    if (!response.ok) return "";
    const data = await response.json();
    const root = asRecord(data);
    const nested = asRecord(root.data);
    const useNested = Boolean(
      coerceId(nested.id ?? nested._id) || String(nested.name ?? nested.title ?? "").trim()
    );
    const source = useNested ? nested : root;
    const profile = asRecord(source.profile);
    const firm = asRecord(source.firmDetails);
    return String(
      profile.companyName || source.name || firm.companyName || source.companyName || ""
    ).trim();
  } catch {
    return "";
  }
}

export async function fetchClientProjectById(token: string, projectId: string): Promise<ClientProjectDetail> {
  const response = await fetch(`${API_URL}/client/projects/${encodeURIComponent(projectId)}`, {
    method: "GET",
    headers: authHeaders(token),
  });
  if (!response.ok) throw new Error(await readError(response, "Failed to load design request"));
  const data = await response.json();
  const root = asRecord(data);
  const nested = asRecord(root.data);
  const useNested = Boolean(
    coerceId(nested.id ?? nested._id) ||
      String(nested.title ?? "").trim() ||
      String(nested.project_number ?? "").trim()
  );
  const source = useNested ? nested : root;
  const office = asRecord(source.office);
  return {
    id: coerceId(source.id ?? source._id) || projectId,
    project_number: String(source.project_number ?? "").trim() || undefined,
    title: String(source.title ?? "").trim() || undefined,
    notes: String(source.notes ?? "").trim() || undefined,
    status: String(source.status ?? "").trim() || undefined,
    vendor_org_id: coerceId(source.vendor_org_id ?? source.organization_id) || undefined,
    designer_name:
      String(source.designer_name ?? source.assigned_to_name ?? "").trim() || undefined,
    assigned_to_name: String(source.assigned_to_name ?? "").trim() || undefined,
    customer_email: String(source.customer_email ?? "").trim() || undefined,
    customer_phone: String(source.customer_phone ?? "").trim() || undefined,
    customer_name: String(source.customer_name ?? "").trim() || undefined,
    first_name: String(source.first_name ?? "").trim() || undefined,
    last_name: String(source.last_name ?? "").trim() || undefined,
    customer_global_user_id:
      coerceId(source.customer_global_user_id ?? source.global_user_id) || undefined,
    customer_membership_id:
      coerceId(source.customer_membership_id ?? source.membership_id ?? source.customer_id) || undefined,
    agent_global_user_id: coerceId(source.agent_global_user_id) || undefined,
    team_member_global_user_id: coerceId(source.team_member_global_user_id) || undefined,
    license_type: String(source.license_type ?? "").trim() || undefined,
    agent_membership_id: coerceId(source.agent_membership_id ?? source.agent_id) || undefined,
    agent_name: String(source.agent_name ?? "").trim() || undefined,
    team_member_membership_id: coerceId(source.team_member_membership_id ?? source.team_member_id) || undefined,
    team_member_name: String(source.team_member_name ?? "").trim() || undefined,
    brokerage_firm_organization_id: coerceId(source.brokerage_firm_organization_id ?? source.brokerage_firm_id) || undefined,
    brokerage_firm_name: String(source.brokerage_firm_name ?? "").trim() || undefined,
    office: {
      _id: coerceId(office._id ?? office.id) || undefined,
      officeName: String(office.officeName ?? office.name ?? "").trim() || undefined,
    },
    created_at: String(source.created_at ?? "").trim() || undefined,
    updated_at: String(source.updated_at ?? "").trim() || undefined,
    linked_order_id: coerceId(source.linked_order_id ?? asRecord(source.linked_order).id) || undefined,
    linked_order_number: String(source.linked_order_number ?? asRecord(source.linked_order).number ?? "").trim() || undefined,
    linked_order_type: String(source.linked_order_type ?? "").trim() || undefined,
    approved_proof_versions: asArray(source.approved_proof_versions)
      .map((v) => Number(v))
      .filter((n) => Number.isFinite(n) && n > 0),
    approved_proofs: asArray(source.approved_proofs),
    billing_address: source.billing_address,
    shipping_address: source.shipping_address,
    shipping_method: (source.shipping_method as ClientProjectDetail["shipping_method"]) || null,
    quantity: Number(source.quantity) || undefined,
    design_approved_by: asRecord(source.design_approved_by).name
      ? { name: String(asRecord(source.design_approved_by).name) }
      : undefined,
  };
}

export type CreateClientProjectPayload = Record<string, unknown>;

export async function createClientProject(
  token: string,
  payload: CreateClientProjectPayload,
  files: File[]
): Promise<{ projectId: string; projectNumber?: string }> {
  const formData = new FormData();
  formData.append("payload", JSON.stringify(payload));
  files.forEach((file) => formData.append("files[]", file));

  const response = await fetch(`${API_URL}/client/projects`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    body: formData,
  });
  if (!response.ok) throw new Error(await readError(response, "Failed to create design request"));
  const data = await response.json();
  const root = asRecord(data);
  const project = asRecord(root.project);
  const projectId = coerceId(project.id ?? project._id ?? root.id ?? root._id);
  if (!projectId) throw new Error("Design request created but no id was returned");
  return {
    projectId,
    projectNumber: String(project.project_number ?? "").trim() || undefined,
  };
}

export async function updateClientProject(
  token: string,
  projectId: string,
  payload: CreateClientProjectPayload
): Promise<void> {
  const response = await fetch(`${API_URL}/client/projects/${encodeURIComponent(projectId)}`, {
    method: "PATCH",
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(await readError(response, "Failed to update design request"));
}

export type ClientProjectMessage = {
  id: string;
  sender_name?: string;
  sender_type?: string;
  message?: string;
  created_at?: string;
  reply_to_message_id?: string;
  proof_version?: number | null;
  files: ClientProjectFile[];
};

export type ClientProjectFile = {
  id: string;
  original_name?: string;
  file_name?: string;
  file_size?: number;
  is_proof?: boolean;
  version?: number;
  is_approved?: boolean;
  created_at?: string;
  file_id?: string;
  file_type?: string;
  preview_url?: string;
  file_url?: string;
  quantity?: number;
  created_by_name?: string;
};

function mapFile(row: unknown): ClientProjectFile | null {
  const f = asRecord(row);
  const id = coerceId(f.id ?? f._id ?? f.project_file_id ?? f.file_id);
  if (!id) return null;
  return {
    id,
    original_name: String(f.original_name ?? "").trim() || undefined,
    file_name: String(f.file_name ?? f.name ?? "").trim() || undefined,
    file_size: Number(f.file_size) || 0,
    is_proof: f.is_proof === true,
    version: Number(f.version) || 0,
    is_approved: f.is_approved === true,
    created_at: String(f.created_at ?? "").trim() || undefined,
    file_id: coerceId(f.file_id) || undefined,
    file_type: String(f.file_type ?? "").trim() || undefined,
    preview_url: String(f.preview_url ?? "").trim() || undefined,
    file_url: String(f.file_url ?? "").trim() || undefined,
    quantity: Number.isFinite(Number(f.quantity)) ? Math.max(0, Math.floor(Number(f.quantity))) : 0,
    created_by_name: String(asRecord(f.created_by).name ?? f.created_by_name ?? "").trim() || undefined,
  };
}

export async function fetchClientProjectMessages(token: string, projectId: string): Promise<ClientProjectMessage[]> {
  const response = await fetch(
    `${API_URL}/client/projects/${encodeURIComponent(projectId)}/messages?page=1&limit=100`,
    { method: "GET", headers: authHeaders(token) }
  );
  if (!response.ok) throw new Error(await readError(response, "Failed to load messages"));
  const data = await response.json();
  return asArray(asRecord(data).data).map((row) => {
    const item = asRecord(row);
    const attachedFiles = asArray(item.reply_to_files).length
      ? asArray(item.reply_to_files)
      : asArray(item.files);
    return {
      id: coerceId(item.id ?? item._id),
      sender_name: String(item.sender_name ?? "").trim() || undefined,
      sender_type: String(item.sender_type ?? "").trim() || undefined,
      message: String(item.message ?? "").trim() || undefined,
      created_at: coerceIso(item.created_at) || undefined,
      reply_to_message_id: coerceId(item.reply_to_message_id) || undefined,
      proof_version:
        typeof item.proof_version === "number"
          ? item.proof_version
          : attachedFiles
              .map((fileRow) => Number(asRecord(fileRow).version))
              .find((n) => Number.isFinite(n) && n > 0) || null,
      files: attachedFiles.map(mapFile).filter((f): f is ClientProjectFile => !!f),
    };
  });
}

export async function postClientProjectMessage(
  token: string,
  projectId: string,
  message: string,
  opts?: { reply_to_message_id?: string; reply_to_file_ids?: string[] }
): Promise<void> {
  const payload: Record<string, unknown> = { message };
  const replyTo = String(opts?.reply_to_message_id || "").trim();
  const fileIds = (opts?.reply_to_file_ids || []).map((id) => String(id || "").trim()).filter(Boolean);
  if (replyTo) payload.reply_to_message_id = replyTo;
  if (fileIds.length) payload.reply_to_file_ids = fileIds;
  const response = await fetch(`${API_URL}/client/projects/${encodeURIComponent(projectId)}/messages`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(await readError(response, "Failed to send message"));
}

export async function fetchClientProjectFiles(
  token: string,
  projectId: string
): Promise<{ sharedFiles: ClientProjectFile[]; proofsByVersion: Record<number, ClientProjectFile[]> }> {
  const response = await fetch(`${API_URL}/client/projects/${encodeURIComponent(projectId)}/files`, {
    method: "GET",
    headers: authHeaders(token),
  });
  if (!response.ok) throw new Error(await readError(response, "Failed to load files"));
  const data = await response.json();
  const root = asRecord(data);
  const nested = asRecord(root.data);
  const body = Array.isArray(nested.shared_files) || nested.proofs ? nested : root;
  const sharedFiles = asArray(body.shared_files).map(mapFile).filter((f): f is ClientProjectFile => !!f);
  const proofsByVersion: Record<number, ClientProjectFile[]> = {};
  Object.entries(asRecord(body.proofs)).forEach(([key, value]) => {
    const version = Number(key);
    if (!Number.isFinite(version)) return;
    proofsByVersion[version] = asArray(value).map(mapFile).filter((f): f is ClientProjectFile => !!f);
  });
  return { sharedFiles, proofsByVersion };
}

export async function uploadClientProjectFiles(
  token: string,
  projectId: string,
  files: File[]
): Promise<unknown> {
  const formData = new FormData();
  files.forEach((file) => formData.append("files[]", file));
  const response = await fetch(`${API_URL}/client/projects/${encodeURIComponent(projectId)}/files`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    body: formData,
  });
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const rec = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    const msg = rec.message || rec.error || rec.msg;
    throw new Error(typeof msg === "string" && msg.trim() ? msg : "Failed to upload files");
  }
  return raw;
}

export async function approveClientProjectProof(
  token: string,
  projectId: string,
  body: Record<string, unknown>
): Promise<void> {
  const response = await fetch(`${API_URL}/client/projects/${encodeURIComponent(projectId)}/approve-proof`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await readError(response, "Failed to approve proof"));
}

export function publicWorkDriveDownloadUrl(fileId?: string): string {
  if (!fileId) return "";
  return `${API_URL}/public/work-drive/files/${encodeURIComponent(fileId)}/download`;
}

export function getLinkedOrderPath(
  roleBase: string,
  params: { linked_order_id?: string; linked_order_type?: string }
): string | null {
  const orderId = String(params.linked_order_id ?? "").trim();
  if (!orderId) return null;
  const orderType = String(params.linked_order_type ?? "sales_order").trim().toLowerCase();
  if (orderType === "service_order") {
    return `${roleBase}/jobs/viewInstallation/${encodeURIComponent(orderId)}`;
  }
  return `${roleBase}/orderDetails/${encodeURIComponent(orderId)}`;
}
